// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.AlertBox.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------
/**
 * @module Alert control - Display alert content inside a highlighted box
 * @example
 * <Alert type=['success'| 'info' |'warning'|'error'] dismissible=[true|false] autoHide=[true|false]>
 *   ...
 * </Alert>
 */

import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { ALERT_TYPES, DISPLAY_TIME } from 'logic/data/Izenda.AlertConstants';

@observer
export default class Alert extends Component {
  @observable show: boolean = true;

  static propTypes = {
    type: PropTypes.oneOf([ALERT_TYPES.SUCCESS, ALERT_TYPES.INFO, ALERT_TYPES.WARNING, ALERT_TYPES.ERROR]),
    dismissible: PropTypes.bool,
    autoHide: PropTypes.bool,
    displayTime: PropTypes.number,
    onAutoHide: PropTypes.func,
    onDismiss: PropTypes.func
  };

  static defaultProps = {
    dismissible: false,
    autoHide: true,
    displayTime: DISPLAY_TIME
  };

  constructor(props: {}) {
    super(props);
    this.mounted = false;
    this.setTimeout(this.props);
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
    window.clearTimeout(this.myTimeout);
  }

  componentWillReceiveProps(nextProps: {}) {
    window.clearTimeout(this.myTimeout);
    this.show = true;
    this.setTimeout(nextProps);
  }

  setTimeout(props: {}) {
    if (props.autoHide) {
      this.myTimeout = window.setTimeout(() => {
        this.mounted && (this.show = false);
        props.onAutoHide && props.onAutoHide();
      }, props.displayTime);
    }
  }

  closeAlert = () => {
    this.show = false;
    this.props.onDismiss && this.props.onDismiss();
  };

  render() {
    const { dismissible, className, type, children } = this.props;

    const dismissButton = dismissible && (
      <a type='button' className='close' aria-label='Close' onClick={this.closeAlert}>
        <span aria-hidden='true'>&times;</span>
      </a>
    );

    const classes = classnames('izenda-Alert alert', className, {
      'alert-success': type === ALERT_TYPES.SUCCESS,
      'alert-info': type === ALERT_TYPES.INFO,
      'alert-warning': type === ALERT_TYPES.WARNING,
      'alert-danger': type === ALERT_TYPES.ERROR,
      'alert-dismissible': dismissible
    });

    return children && this.show ? (
      <div className={classes} role='alert'>
        {dismissButton}
        {children}
      </div>
    ) : null;
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.Alert.js